/**
 * ManagerManual — /#/manuals/manager
 * Long-scroll, well-typeset HTML manual mirroring the Manager markdown source.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 * M-PACT hyphenated. Bryan K. Short, Founder and CEO.
 * "Compelling Reason to Buy" — never "Compelling event".
 *
 * W66.X3 rewrite — matches current ManagerHub production (verdict/trend/cards/
 * pipeline/flagged/coaching/derailment tabs). PROBABLE bucket removed per canon.
 * CP7/CP8/CP9 body copy corrected from 00_CANON_READ_FIRST.md.
 * Glossary: M-PACT and Composite score defined separately.
 */

import ManualLayout, {
  ManualSectionHeading,
  Callout,
  ManualImage,
  StepList,
  BulletList,
  ManualPara,
  ManualTable,
  type ManualSection,
} from "@/components/manuals/ManualLayout";

import shot01 from "@/assets/manuals/manager/01-dashboard-manager.png";
import shot02 from "@/assets/manuals/manager/02-manager-team-roster.png";
import shot03 from "@/assets/manuals/manager/03-manager-coaching-queue.png";
import shot04 from "@/assets/manuals/manager/04-manager-account-audit.png";
import shot05 from "@/assets/manuals/manager/05-manager-sales-plan-review.png";
import shot06 from "@/assets/manuals/manager/06-manager-forecast.png";

const SECTIONS: ManualSection[] = [
  { id: "welcome",        label: "Welcome and how to use this manual" },
  { id: "role",           label: "The role: what a Manager owns in M-PACT" },
  { id: "login",          label: "Logging in and navigating" },
  { id: "dashboard",      label: "Your dashboard: the ManagerHub tab strip" },
  { id: "coaching",       label: "The coaching cadence" },
  { id: "plan-review",    label: "Reviewing an AE's Top 10: auditing a sales plan" },
  { id: "account-audit",  label: "Auditing an account: walking the CP framework" },
  { id: "forecast",       label: "Forecast: how the Attilius forecast model works" },
  { id: "rhythm",         label: "Weekly and monthly rhythm" },
  { id: "glossary",       label: "Glossary, gotchas, and sign-off" },
];

const BLACK = "#0B0B0F";

export default function ManagerManual() {
  return (
    <ManualLayout
      role="Manager"
      title="Attilius Operating Manual — Manager"
      meta="Manager role · v1.1 · June 2026"
      sections={SECTIONS}
    >
      {/* ── Chapter 1 — Welcome ──────────────────────────────────────────────── */}
      <ManualSectionHeading id="welcome" num={1} title="Welcome and how to use this manual" />

      <ManualPara>
        This manual is for the Manager — the person responsible for a team of Account Executives
        and Business Development Representatives inside Attilius. Where the AE and BDR manuals tell each
        individual contributor what to do, this manual tells you what to watch, what to push, and
        where to intervene.
      </ManualPara>

      <ManualPara>
        Read the manual once end to end in your first week on Attilius. The chapter and section numbers
        are fixed — when Bryan or your team refers to "the CP framework walk in Chapter 7," you will
        know exactly where that is.
      </ManualPara>

      <ManualPara>
        Attilius uses the word Manager for this role throughout the platform. The locked role labels are
        manager, ae, bdr, se, marketing, and executive.
      </ManualPara>

      {/* ── Chapter 2 — Role ─────────────────────────────────────────────────── */}
      <ManualSectionHeading id="role" num={2} title="The role: what a Manager owns inside M-PACT" />

      <ManualPara>
        M-PACT is the Mi6 operating model. The Manager owns all five pillars at the team level
        rather than the account level.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M — Methodology.</strong> You are the enforcer of the 10 checkpoints. An AE does not move a deal forward without having the CP evidence. Your job is to audit that evidence, not accept a verbal summary. The CP framework is not a checklist the AE fills out once — it is a live diagnostic you review at every 1:1.</span>,
          <span><strong style={{ color: BLACK }}>P — Process / Prioritization.</strong> You own the Sales Plan workbook for each AE. When a plan is stale, forecast is fiction. Keeping plans current is a management responsibility, not an AE responsibility.</span>,
          <span><strong style={{ color: BLACK }}>A — Accountability.</strong> The Per-rep cards tab on your dashboard shows Composite average score, deal count, and activity health for every AE on your team. Your response to red numbers is what defines you as a manager.</span>,
          <span><strong style={{ color: BLACK }}>C — Cadence / Coaching.</strong> Attilius tracks days since the last coaching session for each AE. Target is 7 days or fewer. An AE at 14 days is past threshold. The coaching cadence is not informal conversation — it is a structured 1:1 with a prepared agenda drawn from the AE's dashboard data.</span>,
          <span><strong style={{ color: BLACK }}>T — Timing / Timeline management.</strong> You own the commitment to close dates. When an AE has a deal with a firm timeline 30 days out and the CP evidence is incomplete, that is your problem to solve before it becomes a miss.</span>,
        ]}
      />

      {/* ── Chapter 3 — Login ────────────────────────────────────────────────── */}
      <ManualSectionHeading id="login" num={3} title="Logging in and navigating" />

      <ManualPara>
        Route — <code>https://aip.mi6group.com</code>. After login, click <strong>Management</strong> in the VIEW AS toggle to activate the manager nav bar. The platform routes you to <code>/#/dashboards/management</code>, which renders the ManagerHub.
      </ManualPara>

      <ManualImage
        src={shot01}
        alt="ManagerHub — Attilius Management view"
        caption="Live screen: /#/dashboards/management — Activity Verdict tab active, demo data loaded"
      />

      <ManualTable
        headers={["Task", "Where to go"]}
        rows={[
          ["Morning read — who is INSUFFICIENT today", "ManagerHub → Activity Verdict tab"],
          ["Team activity trends over time", "ManagerHub → Trend tab"],
          ["Per-AE health cards and Composite scores", "ManagerHub → Per-rep cards tab"],
          ["Pipeline deals in motion", "ManagerHub → Pipeline tab"],
          ["Flagged deals needing attention", "ManagerHub → Flagged tab"],
          ["Log or review coaching notes", "ManagerHub → Coaching tab"],
          ["Deals at risk of derailing", "ManagerHub → Derailment Watch tab"],
          ["Audit a specific deal's CP evidence", "/#/audit-review (standalone page)"],
          ["Review an AE's Top-10 accounts", "/#/sales-plan (standalone page)"],
          ["Check plan vs actual attainment", "/#/plan-vs-actual (standalone page)"],
          ["Walk a deal's qualification path visually", "/#/opportunity-dna (standalone page)"],
        ]}
      />

      {/* ── Chapter 4 — Dashboard ────────────────────────────────────────────── */}
      <ManualSectionHeading id="dashboard" num={4} title="Your dashboard: the ManagerHub tab strip" />

      <ManualPara>
        Route — <code>/#/dashboards/management</code>. The ManagerHub opens on the Activity Verdict
        tab. A red badge on the tab label shows how many people on your team are INSUFFICIENT for
        the current day. Work through the tabs in the order below every morning.
      </ManualPara>

      <ManualImage
        src={shot02}
        alt="ManagerHub — Activity Verdict tab with team status"
        caption="Live screen: /#/dashboards/management — Activity Verdict tab showing per-AE daily status."
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4A — Activity Verdict tab
      </h3>

      <ManualPara>
        The default tab. Shows the activity verdict for each AE on the team for the current day.
        Possible states are <strong>Sufficient</strong> and <strong>Insufficient</strong>. The badge
        count on the tab label is the number of INSUFFICIENT people right now. Any INSUFFICIENT AE
        is an immediate coaching signal — open the Coaching tab and log a note before the day is out.
      </ManualPara>

      <ManualPara>
        The verdict is the platform's "no hiding" mechanism. An AE who is behind on activity cannot
        be obscured by a high Composite score. The verdict cuts through deal-level optimism and
        surfaces behavioral reality.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4B — Trend tab
      </h3>

      <ManualPara>
        The Trend tab shows the team's rolling activity chart over time plus a per-AE mini sparkline
        grid. Each sparkline card links to that AE's Pipeline DNA view at <code>/#/pipeline-dna/[ae-name]</code>.
        Use the Trend tab weekly to identify whether the team's activity volume is growing, flat, or
        contracting. A contracting trend 3 weeks out from quarter end is a pipeline problem — diagnose
        it from the Trend tab before it becomes a forecast problem.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4C — Per-rep cards tab
      </h3>

      <ManualPara>
        The Per-rep cards tab renders the full AE card grid. Each card shows the AE's Composite
        average score, deal count, and current activity health. Use this tab to identify which AEs
        have the lowest Composite average scores — those AEs have the least-qualified pipeline and
        are the highest coaching priority. Any Composite average below 50 means the AE's active
        deals are under-qualified.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4D — Pipeline tab
      </h3>

      <ManualPara>
        The Pipeline tab shows deals in motion across the team. Use it to scan for deals that have
        been in the same forecast bucket for more than 30 days without CP progress. A deal that
        does not move is either stuck or dead — the pipeline tab surfaces the signal; the Coaching
        tab is where you act on it.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4E — Flagged tab
      </h3>

      <ManualPara>
        The Flagged tab surfaces deals and accounts that have been explicitly flagged — either by
        the platform's risk logic or by a team member. Work top to bottom. A flagged deal that has
        no response logged is a coaching gap.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4F — Coaching tab
      </h3>

      <ManualPara>
        The Coaching tab is where you log and review coaching notes tied to specific AEs. Notes
        are visible to the AE and to you. Every 1:1 should produce at least one logged coaching
        note. The absence of notes is the absence of a record — if it is not logged, it did not
        happen as far as the platform is concerned.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        4G — Derailment Watch tab
      </h3>

      <ManualPara>
        The Derailment Watch tab surfaces deals that show the behavioral and scoring patterns
        associated with deals that slip or die. These are not necessarily flagged deals — they are
        deals whose trajectory matches historical derailment signatures. Review this tab in your
        weekly pipeline prep. Any deal on the Derailment Watch list that is also in Commit requires
        an immediate 1:1.
      </ManualPara>

      {/* ── Chapter 5 — Coaching Cadence ─────────────────────────────────────── */}
      <ManualSectionHeading id="coaching" num={5} title="The coaching cadence: who you meet with, when, and what to bring" />

      <ManualPara>
        Route — <code>/#/dashboards/management</code> (Coaching tab). The tab shows coaching notes
        logged for your AEs. Use the Activity Verdict tab to identify who is behind; use the Coaching
        tab to log your response.
      </ManualPara>

      <ManualImage
        src={shot03}
        alt="Coaching tab — coaching notes feed"
        caption="Live screen: /#/dashboards/management — Coaching tab. Add a note, select an AE, log the coaching record."
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        5A — Weekly 1:1
      </h3>

      <ManualPara>
        Every AE gets a weekly 1:1. Standard cadence is 30–45 minutes. Do not run a 1:1 without
        preparing from Attilius data first.
      </ManualPara>

      <StepList
        steps={[
          <span>Pull the AE's Activity Verdict from the Activity Verdict tab — INSUFFICIENT on two or more recent days is the first agenda item.</span>,
          <span>Pull the AE's Composite average from the Per-rep cards tab — any score below 50 means you are reviewing that deal together in the 1:1.</span>,
          <span>Check the Flagged tab for any account owned by this AE.</span>,
          <span>Check the Derailment Watch tab for any deal owned by this AE.</span>,
          <span>Pull the AE's CP answer history from <code>/#/audit-review</code> — any CP answer that is blank, dated more than 14 days ago, or flagged as uncertain becomes a coaching question.</span>,
        ]}
      />

      <ManualPara>
        The structure of every 1:1: activity health (no INSUFFICIENT days), pipeline health (right
        forecast buckets), CP evidence review (two lowest-scoring CPs on the highest-value deal),
        next 30 days (where the AE needs support). Log the outcome in the Coaching tab before the
        1:1 ends.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        5B — Bi-weekly pipeline review
      </h3>

      <ManualPara>
        All AEs attend. The pipeline review is not a status meeting — it is a deal review. Each AE
        brings their top two qualified deals and walks the CP evidence. Peer accountability is the
        mechanism. Prepare by opening the Pipeline tab (identify deals stalled in a bucket), the
        Derailment Watch tab (current at-risk list), and the Per-rep cards tab (identify the two
        deals with highest dollar value and lowest Composite score).
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        5C — Monthly close plan review
      </h3>

      <ManualPara>
        Stress-test each close date against CP evidence. For each deal in the close plan: Is there
        a signed order form or MSA in CP10 Legal — Contracts, SLAs, Terms? Is the Authority contact
        (CP5 — Authority: Project / Initiative) the person who will actually sign? Is the firm
        timeline under CP4 a real date with a real consequence, stated by the buyer — not an AE
        assumption? Any deal with a CP10 score of zero is not a close — move it to Best Case at best.
      </ManualPara>

      {/* ── Chapter 6 — Plan Review ──────────────────────────────────────────── */}
      <ManualSectionHeading id="plan-review" num={6} title="Reviewing an AE's Top 10: how to audit a sales plan" />

      <ManualPara>
        Route — <code>/#/sales-plan</code>. As of June 2026, the Sales Plan is a standalone page
        accessible to all roles including Manager. The manager view includes a Quota Workbook tab
        showing all AEs' Top-10 accounts in a rolled-up grid. Use the VIEW AS toggle to switch to
        an individual AE's plan for a focused review.
      </ManualPara>

      <ManualImage
        src={shot05}
        alt="Sales Plan — manager quota workbook view"
        caption="Live screen: /#/sales-plan — Manager Quota Workbook tab showing all AEs' Top-10 accounts."
      />

      <StepList
        steps={[
          <span>Navigate to <code>/#/sales-plan</code>. Open the Quota Workbook tab to see all AEs' Top-10 accounts in one view.</span>,
          <span>Identify which AEs have fewer than 10 accounts in their plan, or whose total committed revenue is below 1.5x quota — these are under-built plans.</span>,
          <span>Use the VIEW AS toggle to switch to a specific AE's plan for a detailed review.</span>,
          <span>For each account in the AE's Top 10, check: account is real (a named company with a qualified contact), revenue target is committed, CP1 is answered, a champion is named, and a close quarter is set.</span>,
          <span>Push back on any account that fails two or more checks. An account with no CP1, no champion, and no close quarter is a wish list, not a plan.</span>,
          <span>Review the AE's total committed revenue across the Top 10 versus their quota. The rule of thumb is 2x quota in Top-10 committed pipeline. Below 1.5x is an immediate plan-building conversation.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Questions to ask in the plan review
      </h3>

      <BulletList
        items={[
          <span>"Walk me through why this account is in your Top 10 and not in the next tier."</span>,
          <span>"Who is the champion in this account, and when did you last speak with them."</span>,
          <span>"What is the firm timeline on the primary deal. What happens if the date slips."</span>,
          <span>"Is the budget real — has the buyer named the line item or the approval process."</span>,
          <span>"If this deal slips to next quarter, what do you pull forward to replace it."</span>,
        ]}
      />

      {/* ── Chapter 7 — Account Audit ────────────────────────────────────────── */}
      <ManualSectionHeading id="account-audit" num={7} title="Auditing an account: walking the CP framework with an AE" />

      <ManualPara>
        Route — <code>/#/audit-review</code>. This is a standalone page — not a tab on the
        management dashboard. The audit workflow walks a specific deal checkpoint by checkpoint
        together with the AE. Open it alongside the Opportunity DNA view at{" "}
        <code>/#/opportunity-dna</code> for the full visual context.
      </ManualPara>

      <ManualImage
        src={shot04}
        alt="Account audit — CP framework review and Opportunity DNA"
        caption="Live screen: /#/opportunity-dna — Opportunity DNA showing CP1–CP10 checkpoint health for a selected deal."
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        Walking the checkpoints — in pairs
      </h3>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>CP1–CP2: Project and Need.</strong> CP1 — Account Profile: is there a real project with defined scope; is this a new logo or an existing customer expansion? CP2 — Compelling Pain: pain named and quantified in the buyer's own words, corroborated by a second source, with a demonstration that the solution addresses it. If neither has specific, current evidence, the deal should not be in any active forecast bucket.</span>,
          <span><strong style={{ color: BLACK }}>CP3–CP4: Budget and Timeline.</strong> CP3: real money with a named source and validated line item — not "they have budget." The three valid states are Formal (named, allocated line item), Discretionary (carved from an existing budget the buyer controls), and No Budget (buyer must build the internal case first). CP4: decision window and implementation window both real and stated by the buyer — a firm, buyer-stated date with a real consequence if it slips, not an AE assumption.</span>,
          <span><strong style={{ color: BLACK }}>CP5–CP6: Authority.</strong> CP5 — Strategic Fit: the project sponsor — the person whose name is on the initiative, who can begin work at an approval level — is named and engaged. This is not the same as the signer. CP6 — Solution Fit: technical sign-off (CTO, IT lead, security, or delegated reviewer) and financial sign-off (CFO, controller, financial signing authority) are both identified. Both must clear before the deal closes.</span>,
          <span><strong style={{ color: BLACK }}>CP7–CP8: Org and Competition.</strong> CP7 — Org Structure and Influence Map: the org chart is understood and the real influence map is documented — where the power actually sits vs. what the official chart shows, including champions, supporters, influencers, and detractors. This is the hidden-risk area; political vetos from people the AE never identified are the most common cause of late-stage loss. CP8 — Competitive Position: the competitive situation is assessed and documented. Two types: (1) a named competitor doing what Attilius does; (2) the buyer's existing infrastructure — Excel, manual workarounds, legacy tools — doing what Attilius would replace. Both are real competition. The AE names which one and positions accordingly.</span>,
          <span><strong style={{ color: BLACK }}>CP9–CP10: Compliance and Legal.</strong> CP9 — Risk and Stakeholder Alignment: IT internal compliance requirements AND industry-specific compliance requirements are both documented. This is the technical compliance gate and it must be met before legal can close. Do not let the AE surprise procurement. CP10 — Commitment to Buy: contracts, service-level agreements, terms and conditions, and the paper path are all mapped. Nothing financial or technical at this point — those were cleared upstream. CP10 is paper. Every barrier to signature is mapped with a path through each.</span>,
        ]}
      />

      <ManualTable
        headers={["Composite score", "Forecast bucket", "Weight applied"]}
        rows={[
          ["80 and above", "Commit", "Score-driven; manager confirms"],
          ["65–79", "Upside", "Score-driven"],
          ["50–64", "Best Case", "Score-driven"],
          ["30–49", "Pipeline", "Score-driven"],
          ["Below 30", "Omit", "Score or hard stop — walk away"],
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Bucket correction rule.</strong> If the AE has the deal
        in Commit but the score is 65, move it to Upside. This is not a punitive move — it is an
        honest move. A Commit deal that slips does more damage to the team's credibility than an
        Upside deal that accelerates. The score is the arbiter. A logged override is allowed but
        it surfaces in coaching.
      </Callout>

      {/* ── Chapter 8 — Forecast ─────────────────────────────────────────────── */}
      <ManualSectionHeading id="forecast" num={8} title="Forecast: how the Attilius forecast model works" />

      <ManualPara>
        Route — <code>/#/plan-vs-actual</code>. Forecast is the translation of pipeline data into
        a number the team will defend to leadership. The plan-vs-actual page is a standalone surface —
        not a tab on the management dashboard.
      </ManualPara>

      <ManualImage
        src={shot06}
        alt="Plan vs Actual — team attainment against quota"
        caption="Live screen: /#/plan-vs-actual — team attainment, weighted pipeline, and gap to plan."
      />

      <ManualTable
        headers={["Bucket", "Composite score range", "Platform behavior"]}
        rows={[
          ["Won", "Closed", "100% — counted in full"],
          ["Commit", "80 and above", "Score-driven assignment; manager confirms"],
          ["Upside", "65–79", "Score-driven assignment"],
          ["Best Case", "50–64", "Score-driven assignment"],
          ["Pipeline", "30–49", "Score-driven assignment"],
          ["Omit", "Below 30 or hard stop", "Explicit walk-away — not in forecast"],
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        What to trust in Commit
      </h3>

      <BulletList
        items={[
          <span>Composite score is at or above 80.</span>,
          <span>Close date is within the current quarter.</span>,
          <span>CP5 (project sponsor) and CP10 (legal / contracts) both have current, specific evidence.</span>,
          <span>The AE can name the firm timeline and the buyer has confirmed it in their own words.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        What to challenge
      </h3>

      <BulletList
        items={[
          <span>Any Commit deal where the Composite score is in the 60s or 70s and the AE moved it manually.</span>,
          <span>Any Commit deal where the close date has slipped once already this quarter.</span>,
          <span>Any Commit deal where CP10 Legal — Contracts, SLAs, Terms is blank.</span>,
          <span>Any Upside deal where the timeline is "end of quarter" with no firm, buyer-stated date.</span>,
          <span>Any Upside deal scheduled to close within 30 days — Upside scoring means the evidence is insufficient to close in 30 days.</span>,
        ]}
      />

      <ManualPara>
        The manager's forecast commitment to leadership is the Commit total plus a discretionary
        call on which Upside deals will pull in. Never commit Best Case deals to leadership as
        closes. Present the forecast in four numbers: Won to date · Commit · Upside call · Gap to plan.
      </ManualPara>

      {/* ── Chapter 9 — Rhythm ───────────────────────────────────────────────── */}
      <ManualSectionHeading id="rhythm" num={9} title="Weekly and monthly rhythm" />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>
        Daily
      </h3>
      <StepList
        steps={[
          <span>Open the ManagerHub at <code>/#/dashboards/management</code>.</span>,
          <span>Check the Activity Verdict tab — note anyone who is INSUFFICIENT. Log a coaching note for each INSUFFICIENT AE before the end of day.</span>,
          <span>Scan the Flagged tab — any new flagged deals or accounts.</span>,
          <span>Check the Derailment Watch tab — any new entries that are also in Commit require same-day action.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Weekly
      </h3>
      <BulletList
        items={[
          <span><strong>Monday.</strong> Pull the Per-rep cards tab. Identify any AE whose Composite average dropped more than 5 points week over week. That is a deal that deteriorated — find out why before the pipeline review.</span>,
          <span><strong>Mid-week.</strong> Conduct 1:1s. Log the outcome in the Coaching tab. Review the Trend tab to confirm the team's activity trajectory is not contracting.</span>,
          <span><strong>Friday.</strong> Pipeline review with the full team. Confirm the forecast number the team is committing for the week. Check <code>/#/plan-vs-actual</code> for week-to-date attainment.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Monthly
      </h3>
      <BulletList
        items={[
          <span>Review each AE's Sales Plan at <code>/#/sales-plan</code>. Update committed accounts, close quarters, and firm timelines.</span>,
          <span>Conduct the monthly close plan review (Chapter 5C). Stress-test every deal in the coming 30-day close plan against CP evidence at <code>/#/audit-review</code>.</span>,
          <span>Review <code>/#/plan-vs-actual</code> for month-to-date. Note which AEs need a realignment conversation before month end.</span>,
          <span>Update the team narrative — why is the top AE at 165% and the bottom at 59%. The narrative is diagnosis, not spin.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Quarterly
      </h3>
      <BulletList
        items={[
          <span>Full Sales Plan review with every AE at <code>/#/sales-plan</code>. Revise the Top-10 accounts for the coming quarter.</span>,
          <span>Re-forecast the annual number based on actual attainment and current pipeline at <code>/#/plan-vs-actual</code>.</span>,
          <span>CP health audit across all active deals at <code>/#/audit-review</code>. Any deal in the same forecast bucket for more than 45 days without CP progress either gets accelerated or omitted.</span>,
          <span>Publish the quarter recap to the Executive role: plan vs actual attainment, forecast accuracy, top three wins, top three losses with root cause, and the coaching action taken on the bottom AE.</span>,
        ]}
      />

      {/* ── Chapter 10 — Glossary ────────────────────────────────────────────── */}
      <ManualSectionHeading id="glossary" num={10} title="Glossary, gotchas, and sign-off" />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M-PACT</strong> — the Mi6 operating methodology. M = Methodology (the 10 checkpoints). P = Process / Prioritization. A = Accountability. C = Cadence / Coaching. T = Timing / Timeline management. M-PACT is the framework; the Composite score is the number it produces. Do not conflate the two.</span>,
          <span><strong style={{ color: BLACK }}>Composite score</strong> — the weighted aggregate of all 10 CP scores for a single deal, expressed on a 0-to-100 scale. Derived under the M-PACT framework. The score drives the deal's forecast bucket and the color shown in Opportunity DNA. It is not a synonym for M-PACT, which is the methodology itself.</span>,
          <span><strong style={{ color: BLACK }}>Forecast bucket</strong> — Commit, Upside, Best Case, Pipeline, or Omit. Assigned by the platform based on Composite score and CP gate state. A manager may override with a logged defense, but the score is the default arbiter.</span>,
          <span><strong style={{ color: BLACK }}>Firm / formal timeline</strong> — the buyer-stated date a project must close by, with a real consequence if it slips. Lives under CP4. A buyer statement, not an AE assumption. The platform uses "firm timeline" and "formal timeline" interchangeably; both mean the buyer has committed a date internally.</span>,
          <span><strong style={{ color: BLACK }}>Compelling Reason to Buy</strong> — the buyer's quantified business pain that makes doing nothing more costly than buying. This is the core of CP2. Do not use "compelling event" on any Attilius surface.</span>,
          <span><strong style={{ color: BLACK }}>ManagerHub</strong> — the management dashboard at <code>/#/dashboards/management</code>. Tabs: Activity Verdict, Trend, Per-rep cards, Pipeline, Flagged, Coaching, Derailment Watch.</span>,
          <span><strong style={{ color: BLACK }}>Activity Verdict</strong> — the platform's daily judgment of whether each AE's activity level is Sufficient or Insufficient. Surfaced on the Activity Verdict tab of ManagerHub. A red badge shows the count of INSUFFICIENT people today.</span>,
          <span><strong style={{ color: BLACK }}>Opportunity DNA</strong> — the AIP visual diagnostic at <code>/#/opportunity-dna</code>. Each level represents a checkpoint. The manager reads it during CP audit sessions. Routes <code>/#/helix</code> and <code>/#/dna</code> redirect here.</span>,
          <span><strong style={{ color: BLACK }}>Omit</strong> — the Mi6-specific forecast bucket for deals the platform or manager has determined should be walked away from. An explicit, defensible choice. Standard industry taxonomy has no equivalent — most CRMs allow dead deals to rot in pipeline. Omit makes the walk explicit.</span>,
          <span><strong style={{ color: BLACK }}>Two-mark rule</strong> — Attilius is the elephant. The AIP helix is the visual diagnostic. Do not mix the two marks.</span>,
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Common gotchas.</strong> The Commit trap: AEs place deals in Commit when they feel good about them, not when the CP evidence warrants it. The Composite score is the arbiter — a score in the 60s or 70s is Upside or Best Case, not Commit. Coaching cadence drift: set a recurring calendar block that cannot be moved; if it is not logged in the Coaching tab, the platform has no record of it. Sales Plan stale data: make plan updates at <code>/#/sales-plan</code> a standing item in every weekly 1:1. INSUFFICIENT activity two days in a row is a same-day coaching conversation, not a Friday agenda item.
      </Callout>

      <Callout>
        <strong style={{ color: BLACK }}>Closing note.</strong> The Opportunity DNA is the diagnostic.
        The path is the play. Bryan K. Short, Founder and CEO, Mi6 Group.
      </Callout>
    </ManualLayout>
  );
}
